import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Chip,
  Button,
  Alert,
} from '@mui/material'
import {
  Refresh,
  Add,
  Edit,
  Delete,
  CardGiftcard,
  FileUpload,
} from '@mui/icons-material'
import { PageShell } from '@components/common/PageShell'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import { themesApi } from '@api/endpoints/themes'
import { AdminThemeDto, CreateThemeRequest } from '@api/types/themes'
import { ThemeFormDialog, ThemeDeleteDialog, ThemeAwardDialog, ThemeImportDialog } from '../components/themes'

/** N/A rather than "Invalid Date" for the rows the API leaves without a timestamp. */
const formatDate = (dateStr?: string) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString()
}

export default function AdminThemesPage() {
  const [themes, setThemes] = useState<AdminThemeDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // Form dialog
  const [formOpen, setFormOpen] = useState(false)
  const [editingTheme, setEditingTheme] = useState<AdminThemeDto | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletingTheme, setDeletingTheme] = useState<AdminThemeDto | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Award dialog
  const [awardOpen, setAwardOpen] = useState(false)
  const [awardTheme, setAwardTheme] = useState<AdminThemeDto | null>(null)

  // Import dialog
  const [importOpen, setImportOpen] = useState(false)

  // Paging state lives here; DataTable never owns it.
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const fetchThemes = useCallback(async () => {
    setLoading(true)
    setFetchError(null)
    try {
      const data = await themesApi.getThemes()
      setThemes(Array.isArray(data) ? data : [])
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to fetch themes')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchThemes()
  }, [fetchThemes])

  const filteredThemes = themes.filter((t) => {
    if (!search) return true
    const q = search.toLowerCase()
    return t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
  })

  const handleCreate = () => {
    setEditingTheme(null)
    setFormError(null)
    setFormOpen(true)
  }

  const handleEdit = (theme: AdminThemeDto) => {
    setEditingTheme(theme)
    setFormError(null)
    setFormOpen(true)
  }

  const handleSave = async (data: CreateThemeRequest) => {
    setSaving(true)
    setFormError(null)
    try {
      if (editingTheme) {
        await themesApi.updateTheme(editingTheme.id, data)
      } else {
        await themesApi.createTheme(data)
      }
      setFormOpen(false)
      fetchThemes()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save theme')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteClick = (theme: AdminThemeDto) => {
    setDeletingTheme(theme)
    setDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingTheme) return
    setDeleting(true)
    setError(null)
    try {
      await themesApi.deleteTheme(deletingTheme.id)
      setDeleteOpen(false)
      setDeletingTheme(null)
      fetchThemes()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete theme')
      setDeleteOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  const handleAwardClick = (theme: AdminThemeDto) => {
    setAwardTheme(theme)
    setAwardOpen(true)
  }

  const columns = useMemo<DataTableColumn<AdminThemeDto>[]>(
    () => [
      {
        key: 'preview',
        header: 'Preview',
        width: 90,
        render: (theme) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: theme.previewPrimary,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: theme.previewSecondary,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          </Box>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        // The card title on a phone.
        primary: true,
        render: (theme) => (
          <Typography variant="body2" fontWeight="bold">
            {theme.name}
          </Typography>
        ),
      },
      {
        key: 'slug',
        header: 'Slug',
        render: (theme) => (
          <Typography variant="body2" color="text.secondary">
            {theme.slug}
          </Typography>
        ),
      },
      {
        key: 'default',
        header: 'Default',
        render: (theme) => (theme.isDefault ? <Chip label="Default" size="small" color="primary" /> : null),
      },
      {
        key: 'sortOrder',
        header: 'Sort Order',
        align: 'right',
        hideBelow: 'md',
        render: (theme) => <Typography variant="body2">{theme.sortOrder}</Typography>,
      },
      {
        key: 'users',
        header: 'Users',
        align: 'right',
        render: (theme) => <Typography variant="body2">{theme.userCount}</Typography>,
      },
      {
        key: 'created',
        header: 'Created',
        hideBelow: 'md',
        render: (theme) => <Typography variant="body2">{formatDate(theme.createdAt)}</Typography>,
      },
    ],
    []
  )

  return (
    <PageShell
      title="Themes"
      subtitle={`${themes.length} theme${themes.length === 1 ? '' : 's'}`}
      actions={
        <>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchThemes}>
            Refresh
          </Button>
          <Button variant="outlined" startIcon={<FileUpload />} onClick={() => setImportOpen(true)}>
            Import
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Create Theme
          </Button>
        </>
      }
    >
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DataTable
        rows={filteredThemes}
        columns={columns}
        getRowKey={(theme) => theme.id}
        loading={loading}
        error={fetchError}
        onRetry={fetchThemes}
        emptyMessage={search ? 'No themes match that search' : 'No themes yet'}
        toolbar={
          <SearchField
            value={search}
            onSearch={(q) => {
              setSearch(q)
              setPage(0) // a narrower result set may not have the page the admin is standing on
            }}
            placeholder="Search by name or slug"
          />
        }
        // No `total` => DataTable pages the rows it was given.
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[10, 25, 50]}
        getRowActions={(theme) => [
          { key: 'edit', label: 'Edit', icon: <Edit fontSize="small" />, onClick: () => handleEdit(theme) },
          {
            key: 'award',
            label: 'Award or revoke',
            hint: 'Give this theme to users, or take it away',
            icon: <CardGiftcard fontSize="small" />,
            onClick: () => handleAwardClick(theme),
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <Delete fontSize="small" />,
            color: 'error',
            onClick: () => handleDeleteClick(theme),
          },
        ]}
      />

      {/* Dialogs */}
      <ThemeFormDialog
        open={formOpen}
        theme={editingTheme}
        saving={saving}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <ThemeDeleteDialog
        open={deleteOpen}
        theme={deletingTheme}
        deleting={deleting}
        onClose={() => { setDeleteOpen(false); setDeletingTheme(null) }}
        onConfirm={handleDeleteConfirm}
      />

      <ThemeAwardDialog
        open={awardOpen}
        theme={awardTheme}
        onClose={() => { setAwardOpen(false); setAwardTheme(null) }}
        onComplete={fetchThemes}
      />

      <ThemeImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={fetchThemes}
      />
    </PageShell>
  )
}
